import { useState } from "react";
import SettingsPage from "../components/SettingsPane/SettingsPage";
import { CheckboxFieldSettings } from "../SettingsScreen";
import ConfirmDialog from "../modelDialogs/ConfirmDialog";
import axios from "../utils/axios";
import { showErrToast, showToast } from "../components/Toasts";
import { inviteOneUser } from "../services/projectUsers";
import { itx } from "../translationItx/itx";
import { saveProject } from "../components/SaveProject";

const pendingInvitedUsersByProject = new Map<string, Array<string>>();

export default function SettingPageEditProject(props: {
  handler: string;
  setRefresh?: (number: number) => void;
  refresh?: number;
}) {
  const { setRefresh, refresh } = props;
  const projectId = state.projectMetadata?.id || "";

  function getInitialPendingInvitedUsers(): Array<string> {
    const savedPendingUsers = pendingInvitedUsersByProject.get(projectId) || [];
    const existingUserEmails = new Set((state.projectMetadata?.users || []).map((u) => (u.email || "").toLowerCase()));
    const filteredPendingUsers = savedPendingUsers.filter(
      (pendingEmail) => !existingUserEmails.has(pendingEmail.toLowerCase()),
    );
    if (projectId) {
      pendingInvitedUsersByProject.set(projectId, filteredPendingUsers);
    }
    return filteredPendingUsers;
  }

  const [users, setUsers] = useState(() => [...(state.projectMetadata?.users || [])]);
  const [pendingInvitedUsers, setPendingInvitedUsers] = useState<Array<string>>(() => getInitialPendingInvitedUsers());
  const [emailToInvite, setEmailToInvite] = useState("");
  const [inviteInProgress, setInviteInProgress] = useState(false);

  function refreshView() {
    setUsers([...(state.projectMetadata?.users || [])]);
    setPendingInvitedUsers((prev) => {
      const existingUserEmails = new Set(
        (state.projectMetadata?.users || []).map((u) => (u.email || "").toLowerCase()),
      );
      const filteredPendingUsers = prev.filter((pendingEmail) => !existingUserEmails.has(pendingEmail.toLowerCase()));
      if (projectId) {
        pendingInvitedUsersByProject.set(projectId, filteredPendingUsers);
      }
      return filteredPendingUsers;
    });
    if (setRefresh && refresh != undefined) {
      setRefresh(refresh + 1);
    }
  }

  function canManageUsers() {
    const projectUsers = state.projectMetadata?.users || [];
    return projectUsers.some(
      (user) => user.email == state.loggedInUser?.email && (user.status == undefined || user.status == "") && !!user.id,
    );
  }

  function askDeleteUser(email: string) {
    state.dialogManager.pushDialog(
      "confirmDeleteProjectUser", // no itx
      <ConfirmDialog
        className="dialog-standard"
        title={itx("inviteUserDialog.userDelete", "User Delete")}
        text={
          itx("inviteUserDialog.removeT1fromProjectT2", `You want to remove this user: ${email}`, {
            T1: email,
            T2: state.projectMetadata?.name || "",
          }) +
          " " +
          itx("inviteUserDialog.saveProject", "Dabei wird das Projekt gespeichert!")
        }
        onOk={() => {
          const payload = {
            emailOfFriend: email?.toLowerCase(),
            projectId: state.projectMetadata?.id,
          };
          axios
            .delete("/api/project/invite/user", { data: payload })
            .then((result) => {
              if (result.data.STATUS === "SUCCESS") {
                state.projectMetadata.users = (state.projectMetadata?.users || []).filter((u) => u.email !== email);
                state.allProjectsMap.set(state.projectMetadata.id, state.projectMetadata);
                state.projectMetadata.lastModifiedLocal = new Date();
                refreshView();
                saveProject();
                showToast({ text: result.data.MESSAGE || itx("success", "Success") });
              } else {
                showErrToast(itx("setPassword.somthingWentwrong", "Something Went wrong."));
              }
            })
            .catch(() => {
              showErrToast(itx("setPassword.somthingWentwrong", "Something Went wrong."));
            });
          return true;
        }}
        onClose={() => true}
        hideXButton={true}
      />,
      true,
      false,
    );
  }

  async function inviteUser() {
    if (inviteInProgress) {
      return;
    }

    setInviteInProgress(true);
    const email = emailToInvite.trim();
    try {
      const project = state.projectMetadata;
      const result = await inviteOneUser({
        project,
        email,
        currentUsers: project?.users || [],
      });

      if (result.status == "SUCCESS") {
        setEmailToInvite("");
        refreshView();
        showToast({ text: result.message });
      } else {
        refreshView();
        showErrToast(result.message);
      }
    } finally {
      setInviteInProgress(false);
    }
  }

  const adminUserId = users[0]?.id;
  const userManagementAllowed = canManageUsers();
  const hasDeletableUser = users.some((user) => (user.email || "") != state.loggedInUser?.email);
  const showDeleteButtons = userManagementAllowed && hasDeletableUser;

  return (
    <SettingsPage
      handler="/settings/editProject" // no itx
    >
      <div className="form-flex">
        <div
          className="form-left"
          style={{ display: "flex", gap: "14px", alignItems: "stretch", width: "100%", flexWrap: "nowrap" }}
        >
          <section className="settings-card" style={{ flex: 1, minWidth: 0 }}>
            <h3 className="settings-card__header">{itx("inviteUserDialog.currentUser", "Aktuelle Mitarbeiter")}</h3>
            <div className="settings-card__body">
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                }}
              >
                {users.length == 0 && (
                  <div>{itx("inviteUserDialog.noInvitedUsers", "Keine ausstehenden Einladungen")}</div>
                )}
                {users.map((user) => {
                  const userEmail = user.email || "";
                  const isCurrentUser = userEmail == state.loggedInUser?.email;
                  const isAdminUser = user.id == adminUserId;
                  return (
                    <div
                      key={userEmail}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "10px",
                        padding: "4px 2px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          minWidth: 0,
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {user.name || userEmail}
                        </div>
                        {isAdminUser && (
                          <div
                            style={{
                              fontSize: "11px",
                              color: "var(--defaultActiveColor)",
                              flexShrink: 0,
                            }}
                            data-tooltip-id="tv-tooltip"
                            data-tooltip-content={itx("InviteUserDialog.isAdminUser", "ist Admin User")}
                            data-tooltip-place="right"
                          >
                            <i className="las la-star" />
                          </div>
                        )}
                      </div>
                      {showDeleteButtons && !isCurrentUser && (
                        <button className="button" onClick={() => askDeleteUser(userEmail)}>
                          <i className="las la-trash" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "14px" }}>
            {pendingInvitedUsers.length > 0 && (
              <section className="settings-card">
                <h3 className="settings-card__header">
                  {itx("inviteUserDialog.invitedUsers", "Ausstehende Einladungen")}
                </h3>
                <div className="settings-card__body">
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                    }}
                  >
                    {pendingInvitedUsers.map((pendingUserEmail) => {
                      return (
                        <div
                          key={pendingUserEmail}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: "10px",
                            padding: "4px 2px",
                          }}
                        >
                          <div
                            style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {pendingUserEmail}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </section>
            )}

            <section className="settings-card">
              <h3 className="settings-card__header">{itx("inviteUserDialog.userInvite", "Mitarbeiter einladen")}</h3>
              <div className="settings-card__body">
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <input
                    className="input"
                    style={{ flex: 1, minWidth: "260px" }}
                    type="email"
                    value={emailToInvite}
                    placeholder={itx("inviteUserDialog.emailAdressOfUser", "E-Mail Adresse des/r Mitarbeiters/in")}
                    onChange={(ev) => setEmailToInvite(ev.target.value)}
                  />
                  <button className="button" onClick={inviteUser} disabled={inviteInProgress}>
                    {itx("inviteUserDialog.inviteAndSave", "Einladen und Projekt speichern")}
                  </button>
                </div>
              </div>
            </section>
          </div>
        </div>
        <div className="form-right"></div>
      </div>
    </SettingsPage>
  );
}
